import { Checkbox, ChoiceGroup, Dropdown, IDropdownOption, TextField } from "@fluentui/react";
import React, { useEffect, useState } from "react";
import { IAcademyQuestion, IAcademyQuestionAlternative } from "src/academy/types";
import { answerPairs, getAnswerPairTexts, isChoiceQuestion, isMissingCorrectAnswer } from "src/academy/utils";
import { localize } from "src/l10n";
import { SpintrTypes } from "src/typings";
import { ActionMenu, Label, UnstyledButton } from "src/ui";
import { FormControl } from "src/ui/components/Forms";
import { uniqueId } from "src/utils";
import "./AcademyTestFormQuestion.scss";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import SmallCreateButton from "../SmallCreateButton/SmallCreateButton";
import { connect } from "react-redux";

interface IProps {
    index: number;
    question: IAcademyQuestion;
    onUpdate: (question: IAcademyQuestion) => void;
    onRemove: () => void;
    onMove: (up: boolean) => void;
    academyAIEnabled?: boolean;
    hideHeader?: boolean;
    isTask?: boolean;
    autoFocus?: boolean;
    showValidation?: boolean;
}

interface IState {
    question: IAcademyQuestion;
    isExpanded: boolean;
}

type QuestionKind = "single" | "multiple" | "trueFalse";

const getQuestionKind = (question: IAcademyQuestion): QuestionKind => {
    if (question.answerPair) {
        return "trueFalse";
    }

    return question.type === SpintrTypes.QuestionType.MultipleAnswer ? "multiple" : "single";
};

const createBlankAlternatives = (): IAcademyQuestionAlternative[] => [
    { id: 0, tmpId: uniqueId(), text: "" },
    { id: 1, tmpId: uniqueId(), text: "" },
];

const createAnswerPairAlternatives = (answerPair: number): IAcademyQuestionAlternative[] =>
    getAnswerPairTexts(answerPair).map((text: string, index: number) => ({
        id: index,
        tmpId: uniqueId(),
        text,
        isCorrect: false,
    }));

const AcademyTestFormQuestion = (props: IProps) => {
    const [state, setState] = useState<IState>({
        question: props.question,
        isExpanded: true,
    });

    useEffect(() => {
        props.onUpdate(state.question);
    }, [state.question]);

    const kind = getQuestionKind(state.question);
    const isLocked = kind === "trueFalse";
    const correctAlternative = state.question.alternatives?.find((a: IAcademyQuestionAlternative) => a.isCorrect);

    const onKindChange = (newKind: QuestionKind) => {
        setState((s: IState) => {
            const currentKind = getQuestionKind(s.question);

            if (newKind === currentKind) {
                return s;
            }

            if (newKind === "trueFalse") {
                return {
                    ...s,
                    question: {
                        ...s.question,
                        type: SpintrTypes.QuestionType.SimpleAnswer,
                        answerPair: answerPairs[0].key,
                        alternatives: createAnswerPairAlternatives(answerPairs[0].key),
                    },
                };
            }

            if (currentKind === "trueFalse") {
                return {
                    ...s,
                    question: {
                        ...s.question,
                        type: newKind === "multiple" ?
                            SpintrTypes.QuestionType.MultipleAnswer :
                            SpintrTypes.QuestionType.SimpleAnswer,
                        answerPair: undefined,
                        alternatives: createBlankAlternatives(),
                    },
                };
            }

            if (newKind === "single") {
                return {
                    ...s,
                    question: {
                        ...s.question,
                        type: SpintrTypes.QuestionType.SimpleAnswer,
                        alternatives: s.question.alternatives.map((a: IAcademyQuestionAlternative) => ({
                            ...a,
                            isCorrect: false,
                        })),
                    },
                };
            }

            return {
                ...s,
                question: {
                    ...s.question,
                    type: SpintrTypes.QuestionType.MultipleAnswer,
                },
            };
        });
    };

    const onAnswerPairChange = (answerPair: number) => {
        setState((s: IState) => {
            const texts = getAnswerPairTexts(answerPair);

            return {
                ...s,
                question: {
                    ...s.question,
                    answerPair,
                    alternatives: s.question.alternatives.map((a: IAcademyQuestionAlternative, index: number) => ({
                        ...a,
                        text: texts[index] ?? a.text,
                    })),
                },
            };
        });
    };

    const setCorrectAlternative = (tmpId: string) => {
        setState((s: IState) => ({
            ...s,
            question: {
                ...s.question,
                alternatives: s.question.alternatives.map((a: IAcademyQuestionAlternative) => ({
                    ...a,
                    isCorrect: a.tmpId === tmpId,
                })),
            },
        }));
    };

    const toggleCorrectAlternative = (tmpId: string, isCorrect: boolean) => {
        setState((s: IState) => ({
            ...s,
            question: {
                ...s.question,
                alternatives: s.question.alternatives.map((a: IAcademyQuestionAlternative) => ({
                    ...a,
                    isCorrect: a.tmpId === tmpId ? isCorrect : a.isCorrect,
                })),
            },
        }));
    };

    const renderAlternativeText = (x: IAcademyQuestionAlternative, index: number) => {
        if (isLocked) {
            return (
                <div className="textFieldWrapper">
                    <Label size="body-2" className="locked-alternative">{x.text}</Label>
                </div>
            );
        }

        return (
            <div className="textFieldWrapper">
                <TextField
                    className="textField"
                    value={x.text}
                    borderless
                    placeholder={localize(props.isTask ? "TASK" : "Alternativ_Singular") + " " + (index + 1) + "..."}
                    onChange={(ev, val) => {
                        setState((s: IState) => {
                            return {
                                ...s,
                                question: {
                                    ...s.question,
                                    alternatives: s.question.alternatives.map((alternative: IAcademyQuestionAlternative) => {
                                        if (alternative.tmpId === x.tmpId) {
                                            return {
                                                ...alternative,
                                                text: val
                                            }
                                        } else {
                                            return alternative;
                                        }
                                    })
                                }
                            }
                        });
                    }}
                    maxLength={128}
                />
            </div>
        );
    };

    const renderRemoveButton = (x: IAcademyQuestionAlternative) => {
        if (isLocked) {
            return null;
        }

        return (
            <UnstyledButton onClick={() => {
                setState((s: IState) => {
                    return {
                        ...s,
                        question: {
                            ...s.question,
                            alternatives: s.question.alternatives.filter(a => a.tmpId !== x.tmpId)
                        }
                    }
                });
            }}>
                <Visage2Icon icon="trash" size="small" />
            </UnstyledButton>
        );
    };

    const renderAlternatives = () => {
        if (props.isTask) {
            return state.question.alternatives.map((x: IAcademyQuestionAlternative, index: number) => (
                <div className="alternative" key={x.tmpId}>
                    {renderAlternativeText(x, index)}
                    {renderRemoveButton(x)}
                </div>
            ));
        }

        if (kind === "multiple") {
            return state.question.alternatives.map((x: IAcademyQuestionAlternative, index: number) => (
                <div className="alternative" key={x.tmpId}>
                    <Checkbox
                        checked={!!x.isCorrect}
                        ariaLabel={x.text || (localize("Alternativ_Singular") + " " + (index + 1))}
                        onChange={(ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
                            toggleCorrectAlternative(x.tmpId, !!checked);
                        }}
                    />
                    {renderAlternativeText(x, index)}
                    {renderRemoveButton(x)}
                </div>
            ));
        }

        return (
            <ChoiceGroup
                className="alternative-choices"
                selectedKey={correctAlternative?.tmpId ?? null}
                options={state.question.alternatives.map((x: IAcademyQuestionAlternative, index: number) => ({
                    key: x.tmpId,
                    text: "",
                    ariaLabel: x.text || (localize("Alternativ_Singular") + " " + (index + 1)),
                    onRenderField: (fieldProps, render) => (
                        <div className="alternative">
                            {render(fieldProps)}
                            {renderAlternativeText(x, index)}
                            {renderRemoveButton(x)}
                        </div>
                    ),
                }))}
                onChange={(ev, option) => {
                    setCorrectAlternative(option.key);
                }}
            />
        );
    };

    return (
        <div className="AcademyTestFormQuestion">
            {!props.hideHeader && (
                <UnstyledButton className="top-row" onClick={() => {
                    setState((s: IState) => {
                        return {
                            ...s,
                            isExpanded: !s.isExpanded
                        }
                    });
                }}>
                    <div className="question-order">
                        <UnstyledButton onClick={() => props.onMove(true)}>
                            <Visage2Icon icon="arrow-up-2" size="small" />
                        </UnstyledButton>
                        <UnstyledButton onClick={() => props.onMove(false)}>
                            <Visage2Icon icon="arrow-down-1" size="small" />
                        </UnstyledButton>
                    </div>
                    <Visage2Icon icon="information" size="small" type="bold" />
                    <Label size="body-2" weight="medium">{state.question.text || (localize("Fraga") + " " + (props.question.number))}</Label>
                    <UnstyledButton onClick={props.onRemove}>
                        <Visage2Icon icon="trash" size="small" />
                    </UnstyledButton>
                </UnstyledButton>
            )}
            {state.isExpanded && (
                <div className="content">
                    <div className="question-top-row">
                        <FormControl className="question-field">
                            <TextField
                                className="textField"
                                value={state.question.text}
                                placeholder={localize(props.isTask ? "Beskrivning" : "Fraga") + "..."}
                                label={localize(props.isTask ? "Beskrivning" : "Fraga")}
                                onChange={(ev, val) => {
                                    setState((s: IState) => {
                                        return {
                                            ...s,
                                            question: {
                                                ...s.question,
                                                text: val
                                            }
                                        }
                                    });
                                }}
                                maxLength={128}
                                autoFocus={props.autoFocus}
                            />
                        </FormControl>
                        {!props.isTask && isChoiceQuestion(state.question) && (
                            <FormControl className="question-type">
                                <Dropdown
                                    ariaLabel={localize("ValjTyp")}
                                    selectedKey={kind}
                                    options={[
                                        { key: "single", text: localize("Enkelvalsfraga") },
                                        { key: "multiple", text: localize("Flervalsfraga") },
                                        { key: "trueFalse", text: localize("ACADEMY_QUESTION_TYPE_TRUE_FALSE") },
                                    ]}
                                    onChange={(event: React.FormEvent<HTMLDivElement>, option: IDropdownOption) => {
                                        onKindChange(option.key as QuestionKind);
                                    }}
                                />
                            </FormControl>
                        )}
                    </div>
                    {state.question.type === SpintrTypes.QuestionType.FreeText && (
                        <FormControl>
                            <TextField
                                className="textField"
                                value={state.question.correctAnswer}
                                placeholder={localize("CORRECT_ANSWER") + "..."}
                                label={localize("CORRECT_ANSWER")}
                                onChange={(ev, val) => {
                                    setState((s: IState) => {
                                        return {
                                            ...s,
                                            question: {
                                                ...s.question,
                                                correctAnswer: val
                                            }
                                        }
                                    });
                                }}
                                maxLength={128}
                            />
                        </FormControl>
                    )}
                    {isLocked && (
                        <FormControl label={localize("ACADEMY_ANSWER_PAIR")} className="answer-pair">
                            <Dropdown
                                selectedKey={state.question.answerPair}
                                options={answerPairs.map((pair) => ({
                                    key: pair.key,
                                    text: getAnswerPairTexts(pair.key).join(" / "),
                                }))}
                                onChange={(event: React.FormEvent<HTMLDivElement>, option: IDropdownOption) => {
                                    onAnswerPairChange(Number(option.key));
                                }}
                            />
                        </FormControl>
                    )}
                    {isChoiceQuestion(state.question) && (
                        <div>
                            {state.question.alternatives.length > 0 && (
                                <FormControl label={localize(props.isTask ? "TASKS" : "Alternativ")} className="alternatives">
                                    {renderAlternatives()}
                                </FormControl>
                            )}
                            {!isLocked && (
                                <SmallCreateButton
                                    text={localize(props.isTask ? "ADD_TASK" : "ADD_ALTERNATIVE")}
                                    onClick={() => {
                                        setState((s: IState) => {
                                            return {
                                                ...s,
                                                question: {
                                                    ...s.question,
                                                    alternatives: [
                                                        ...s.question.alternatives,
                                                        {
                                                            id: s.question.alternatives.reduce((max, a) => (a.id > max ? a.id : max), 0) + 1,
                                                            tmpId: uniqueId(),
                                                            text: ""
                                                        }
                                                    ]
                                                }
                                            }
                                        })
                                    }}
                                />
                            )}
                        </div>
                    )}
                    {props.showValidation && !props.isTask && isMissingCorrectAnswer(state.question) && (
                        <Label size="body-2" color="red" className="validation-error">
                            {localize("ACADEMY_QUESTION_MISSING_CORRECT_ANSWER")}
                        </Label>
                    )}
                </div>
            )}
        </div>
    )
}

const mapStateToProps = (state: Spintr.AppState, props) => ({
    ...props,
    academyAIEnabled: state.instance.get("academyAIEnabled")
});

export default connect(mapStateToProps)(AcademyTestFormQuestion);
